import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { Transition } from "components"
import { Pencil, Plus } from "components/icons/outline"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import { deleteSuratJalanUmum, printSuratJalanUmumUrl, printSuratJalanUmumFakturUrl } from "./api"

const DIRECTION_FILTERS = [
  { value: "", label: "Semua" },
  { value: "OUT", label: "KELUAR" },
  { value: "IN", label: "MASUK" },
]

function directionLabel(v) {
  return String(v).toUpperCase() === "IN" ? "MASUK" : "KELUAR"
}

// qty DECIMAL(18,3) dari backend ("1.000") → tampil "1", "2.500" → "2,5"
function formatQty(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("id-ID", { maximumFractionDigits: 3 })
}

export default function List() {
  const { user } = useAuth()
  const { setTitle } = useOutletContext()
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const page = searchParams.get("page") || 1
  const direction = searchParams.get("direction") || ""
  const searchParam = searchParams.get("search") || ""
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""

  const [search, setSearch] = useState(searchParam)
  const searchInput = useRef(null)

  useEffect(() => setTitle("Surat Jalan Umum"), [setTitle])
  useEffect(() => setSearch(searchParam), [searchParam])

  const { data: result_set, isLoading, mutate } = useResultSet(
    process.env.REACT_APP_INVENTORY_API_URL,
    "surat-jalan-umum",
    {
      page,
      record_per_page: 10,
      search: searchParam || undefined,
      direction: direction || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }
  )

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  const list = useMemo(() => (Array.isArray(result_set?.data) ? result_set.data : []), [result_set])
  const pagination = result_set?.pagination
  const currentPage = Number(pagination?.page || page || 1)
  const lastPage = Number(pagination?.last_page || 1)
  const total = pagination?.total ?? result_set?.total ?? 0

  const patch = (obj) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(obj).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k)
      else next.set(k, String(v))
    })
    if ("search" in obj || "direction" in obj || "date_from" in obj || "date_to" in obj) next.delete("page")
    setSearchParams(next)
  }

  const onDelete = async (row) => {
    if (!window.confirm(`Hapus Surat Jalan ${row.doc_number}?`)) return
    try {
      await deleteSuratJalanUmum(row.id, user?.name)
      toast.success("Dokumen dihapus")
      mutate()
    } catch (err) {
      toast.error(err?.message || "Gagal menghapus")
    }
  }

  return (
    <Transition type="fade">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") patch({ search, page: 1 })
              }}
              placeholder="Cari no. surat / pengirim / penerima"
              className="px-4 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
            />
            <select
              value={direction}
              onChange={(e) => patch({ direction: e.target.value })}
              className="px-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none"
            >
              {DIRECTION_FILTERS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
            <div className="inline-flex items-center gap-2">
              <label className="text-xs text-neutral-500">Dari</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => patch({ date_from: e.target.value })}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
              />
              <label className="text-xs text-neutral-500">s/d</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => patch({ date_to: e.target.value })}
                className="px-3 py-2 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
              />
            </div>
          </div>

          <Link
            to="/surat-jalan-umum/create"
            state={{ transition: "slide" }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-brand-900 hover:bg-brand-800 rounded-xl"
          >
            <Plus className="w-4 h-4" stroke={1.5} />
            Tambah
          </Link>
        </div>

        <div className="overflow-x-auto bg-white border border-neutral-200 rounded-2xl">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">No. Surat</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Tanggal</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Arah</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Dari</th>
                <th className="px-4 py-3 text-xs font-medium text-left uppercase text-neutral-500">Kepada</th>
                <th className="px-4 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-sm text-center text-neutral-500">
                    {isLoading ? "Memuat..." : "Belum ada dokumen."}
                  </td>
                </tr>
              ) : (
                list.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-sm font-medium">{row.doc_number}</td>
                    <td className="px-4 py-3 text-sm">{row.doc_date}</td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          String(row.direction).toUpperCase() === "IN"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {directionLabel(row.direction)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{row.from_party || "-"}</td>
                    <td className="px-4 py-3 text-sm">{row.to_party || "-"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3">
                        <a
                          href={printSuratJalanUmumUrl(row.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-neutral-700 hover:underline"
                        >
                          Print
                        </a>
                        <a
                          href={printSuratJalanUmumFakturUrl(row.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-orange-600 hover:underline"
                        >
                          Faktur
                        </a>
                        <button
                          type="button"
                          onClick={() => navigate(`/surat-jalan-umum/${row.id}/edit`, { state: { transition: "slide" } })}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
                        >
                          <Pencil className="w-4 h-4" stroke={1.5} />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          className="text-xs font-semibold text-red-600 hover:underline"
                        >
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-neutral-600">Total {total} dokumen</p>
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => patch({ page: currentPage - 1 })}
              className="px-3 py-1 text-sm border rounded-lg border-neutral-200 disabled:opacity-40"
            >
              Sebelumnya
            </button>
            <span className="text-sm text-neutral-600">
              {currentPage} / {lastPage}
            </span>
            <button
              type="button"
              disabled={currentPage >= lastPage}
              onClick={() => patch({ page: currentPage + 1 })}
              className="px-3 py-1 text-sm border rounded-lg border-neutral-200 disabled:opacity-40"
            >
              Berikutnya
            </button>
          </div>
        </div>
      </div>
    </Transition>
  )
}
